import { catchError } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ExceptionService, PaginatedList, UtilsService } from '../../../xform-compat';
import { RmsBasicCrudService } from '../../components/rms-basic-crud.service';
import {
  SlideDeckComparisonMeta,
  SlideDeckComparisonRequest,
  SlideDeckComparisonResource,
  SlideDeckDeleteResult,
  SlideDeckRequest,
  SlideDeckResource
} from '../../../shared/models';

/**
 * How many affected comparisons the delete-confirm dialog's impact list asks for (RA-2703). The list is a
 * scroll-capped **preview**, so this bounds the payload rather than the reported impact — the dialog's count
 * always comes from the response's `totalRecords`. Stated explicitly so the cap is a decision in this repo
 * instead of whatever `@DefaultValue` the endpoint happens to carry.
 */
const DECK_IMPACT_PAGE_SIZE = 25;

/**
 * Data service for the slide-deck-comparison report page. Fetches a stored comparison by its
 * database id and proxy-streams individual slide images as authenticated blobs from the backend.
 * Bearer auth is injected by the existing auth-interceptor; no per-request auth code is added.
 */
@Injectable({
  providedIn: 'root'
})
export class SlideDeckComparisonService extends RmsBasicCrudService<any> {

  constructor(
    http: HttpClient,
    utilsService: UtilsService,
    exceptionService: ExceptionService
  ) {
    super(http, utilsService, exceptionService);
  }

  /**
   * Fetches the stored comparison by its database id. The returned resource carries the verbatim
   * comparison JSON in `payload` (emitted embedded by the backend via @JsonRawValue) plus extracted
   * camelCase scalar columns.
   * @param {number} id the comparison database id
   * @returns {Observable<SlideDeckComparisonResource>}
   */
  public getComparison (id: number): Observable<SlideDeckComparisonResource> {
    return super
      .getHttp()
      .get<SlideDeckComparisonResource>(`/api/rms/slide-deck-comparisons/${id}`)
      .pipe(catchError(ex => super.getExceptions().catchBadResponse(ex)));
  }

  /**
   * Fetches the stored comparisons for a single company as a paginated list. The verbatim `payload`
   * is omitted (`include_payload=false`) for performance — each record is the lightweight
   * {@link SlideDeckComparisonMeta} projection. The caller passes `company_id` plus standard
   * `page`/`pagesize` paging params and may narrow by `deck_type`; the Company Decks modal uses this to
   * drive the server-paginated Comparisons table.
   * @param {number} companyId the owning company id
   * @param {*} [params] extra query params (e.g. page, pagesize, deck_type); merged with company_id + include_payload
   * @returns {Observable<PaginatedList<SlideDeckComparisonMeta>>}
   */
  public getComparisons (companyId: number, params?: any): Observable<PaginatedList<SlideDeckComparisonMeta>> {
    const query = { ...(params ?? {}), company_id: companyId, include_payload: false };
    return super.getData('/api/rms/slide-deck-comparisons', query);
  }

  /**
   * Fetches a company's slide decks as a paginated list (RA-2079). Each record is a normalized
   * {@link SlideDeckResource} — one row per physical deck (a deck no longer carries a single OLD/NEW
   * status; that now lives on the link when nested under a comparison). The backend resolves
   * `company_id` against the deck's own company column (RA-2255), so a deck with no comparison at all is
   * still returned for its company. The caller passes standard `page`/`pagesize` paging params and may
   * narrow by `deck_type` (or pass `unresolved: true` to list the decks with no company); `company_id` is
   * always added. This backs the Company Decks modal's Decks section and the company Deck Comparisons
   * panel's deck count, with real server-side pagination.
   * @param {number} companyId the owning company id
   * @param {*} [params] extra query params (e.g. page, pagesize, deck_type); merged with company_id
   * @returns {Observable<PaginatedList<SlideDeckResource>>}
   */
  public getSlideDecks (companyId: number, params?: any): Observable<PaginatedList<SlideDeckResource>> {
    const query = { ...(params ?? {}), company_id: companyId };
    return super.getData('/api/rms/slide-decks', query);
  }

  /**
   * Submits a self-service **deck upload** (RA-2251). The caller assembles the `FormData` with the binary `file`
   * part and the required `metadata` text part (`company_id` + `mnpi_attested`). **No `Content-Type` header is
   * set** — the browser has to write it itself so the multipart boundary is correct. Bearer auth is added by the
   * existing auth-interceptor.
   *
   * EXCEPTION to the feature-wide `catchBadResponse` rule. This endpoint's typed 400s each map to their own line
   * of UI copy (frontend spec §9.2), and `catchBadResponse` discards the `HttpErrorResponse` in favour of a
   * translated string, taking the error code with it. The component reads `err?.error?.code` and maps it;
   * anything unrecognised falls through to `BannerNotificationsService.error`. Do not "fix" this back.
   * @param {FormData} form the assembled multipart body (`file` + `metadata`)
   * @returns {Observable<SlideDeckRequest>} the created request — already terminal for a refusal or a duplicate
   */
  public createDeckRequest (form: FormData): Observable<SlideDeckRequest> {
    return super.getHttp().post<SlideDeckRequest>('/api/rms/slide-deck-requests', form);
  }

  /**
   * Fetches a company's self-service deck upload requests as a paginated list (RA-2251), for the pinned
   * "Uploading…" rows at the top of the decks table. Defaults to `status=PENDING` — a settled request has a real
   * deck row to show instead — but the caller may override it via `params`.
   * @param {number} companyId the owning company id
   * @param {*} [params] extra query params (e.g. page, pagesize, status); merged with company_id
   * @returns {Observable<PaginatedList<SlideDeckRequest>>}
   */
  public getDeckRequests (companyId: number, params?: any): Observable<PaginatedList<SlideDeckRequest>> {
    const query = { status: 'PENDING', ...(params ?? {}), company_id: companyId };
    return super.getData('/api/rms/slide-deck-requests', query);
  }

  /**
   * Fetches a specific set of deck upload requests by id, for the batched status watch that follows a
   * multi-deck upload (RA-2251).
   *
   * 🔴 Deliberately sends **no** `status` param, unlike {@link getDeckRequests}. A settled request drops out
   * of a `status=PENDING` list, so a status-filtered read can only ever observe a row vanishing — never what
   * it became. This is an outcome channel, so it has to be able to see terminal values.
   *
   * `?ids=1&ids=2` is the backend's own contract: `ApplicationFilter` declares `Set<I> ids` and
   * `SlideDeckRequestSpecification` applies `.in(ID_FIELD_NAME, …)`, and `UtilsService.generateParams`
   * appends an array value once per element.
   * @param {number} companyId the owning company id
   * @param {Array<number>} ids the request ids to read
   * @returns {Observable<PaginatedList<SlideDeckRequest>>}
   */
  public getDeckRequestsByIds (companyId: number, ids: Array<number>): Observable<PaginatedList<SlideDeckRequest>> {
    return super.getData('/api/rms/slide-deck-requests', { ids, pagesize: Math.max(1, ids.length), company_id: companyId });
  }

  /**
   * Fetches a single deck upload request by its database id — deliberately **unfiltered by status**, because this
   * is what carries the terminal outcome (its `status`, `failureReason` and resulting `slideDeck`) that the
   * modal's upload notes are written from. A `status=PENDING` list can never deliver that: a settled request
   * simply drops out of it.
   * @param {number} id the request database id
   * @returns {Observable<SlideDeckRequest>}
   */
  public getDeckRequest (id: number): Observable<SlideDeckRequest> {
    return super
      .getHttp()
      .get<SlideDeckRequest>(`/api/rms/slide-deck-requests/${id}`)
      .pipe(catchError(ex => super.getExceptions().catchBadResponse(ex)));
  }

  /**
   * Submits a self-service **comparison** request for two decks RMS already holds (RA-2251). The body is JSON,
   * snake_case to match the endpoint's contract.
   *
   * EXCEPTION to the feature-wide `catchBadResponse` rule, for the same reason as {@link createDeckRequest}: the
   * typed 400s and the 409 that carries an existing comparison each map to their own line of UI copy (frontend
   * spec §11.4), and `catchBadResponse` would discard the code they are keyed on.
   * @param {*} body the request body (`company_id`, `old_slide_deck_id`, `new_slide_deck_id`)
   * @returns {Observable<SlideDeckComparisonRequest>} the created request, `PENDING` until TARA returns
   */
  public createComparisonRequest (body: any): Observable<SlideDeckComparisonRequest> {
    return super.getHttp().post<SlideDeckComparisonRequest>('/api/rms/slide-deck-comparison-requests', body);
  }

  /**
   * Fetches a single comparison request by its database id — unfiltered by status, for the same reason as
   * {@link getDeckRequest}: it is the only transport that can deliver the terminal outcome.
   * @param {number} id the request database id
   * @returns {Observable<SlideDeckComparisonRequest>}
   */
  public getComparisonRequest (id: number): Observable<SlideDeckComparisonRequest> {
    return super
      .getHttp()
      .get<SlideDeckComparisonRequest>(`/api/rms/slide-deck-comparison-requests/${id}`)
      .pipe(catchError(ex => super.getExceptions().catchBadResponse(ex)));
  }

  /**
   * Fetches a company's still-generating self-service comparison requests as a paginated list (RA-2251),
   * for the pinned "Generating…" rows above the comparison list. Defaults to `status=PENDING` — completed
   * requests have a real comparison to show instead — but the caller may override it via `params`.
   * @param {number} companyId the owning company id
   * @param {*} [params] extra query params (e.g. page, pagesize, status); merged with company_id
   * @returns {Observable<PaginatedList<SlideDeckComparisonRequest>>}
   */
  public getRequests (companyId: number, params?: any): Observable<PaginatedList<SlideDeckComparisonRequest>> {
    const query = { status: 'PENDING', ...(params ?? {}), company_id: companyId };
    return super.getData('/api/rms/slide-deck-comparison-requests', query);
  }

  /**
   * Fetches a single slide image as an authenticated blob via the backend S3 proxy. `ref` is the
   * opaque `s3_reference` from the payload — passed verbatim (HttpParams URL-encodes it) and
   * IDOR-validated by the backend against the stored payload. No attachment-download Accept header
   * is sent: the slide is an inline image, not an attachment download.
   * @param {number} id the comparison database id
   * @param {string} ref the opaque slide s3_reference
   * @returns {Observable<Blob>}
   */
  public getSlideImage (id: number, ref: string): Observable<Blob> {
    const params: HttpParams = new HttpParams().set('ref', ref);
    return super
      .getHttp()
      .get(`/api/rms/slide-deck-comparisons/${id}/slides/image`, { params, responseType: 'blob' })
      .pipe(catchError(ex => super.getExceptions().catchBadResponse(ex)));
  }

  /**
   * Fetches a deck PDF as an authenticated blob via the backend S3 proxy, for browser download (RA-2251). Keyed
   * on the deck's own database id — no pre-signed S3 URL, and the bearer token is added by the existing
   * auth-interceptor. The caller triggers the actual save and owns revoking whatever object URL it creates.
   * @param {number} id the slide deck database id
   * @returns {Observable<Blob>}
   */
  public downloadSlideDeck (id: number): Observable<Blob> {
    return super
      .getHttp()
      .get(`/api/rms/slide-decks/${id}/file`, { responseType: 'blob' })
      .pipe(catchError(ex => super.getExceptions().catchBadResponse(ex)));
  }

  /**
   * Soft-deletes a slide deck the signed-in analyst uploaded, along with every comparison built on it
   * (RA-2703). Row-level, not S3: the file stays in storage and re-uploading it restores the deck and its
   * comparisons.
   *
   * The response reports what the **server** actually deactivated, which is what the success note must
   * quote — the confirm dialog's own count came from a snapshot a colleague's ingest can invalidate between
   * render and click.
   * @param {number} deckId the slide deck database id
   * @returns {Observable<SlideDeckDeleteResult>}
   */
  public deleteSlideDeck (deckId: number): Observable<SlideDeckDeleteResult> {
    return super
      .getHttp()
      .delete<SlideDeckDeleteResult>(`/api/rms/slide-decks/${deckId}`)
      .pipe(catchError(ex => super.getExceptions().catchBadResponse(ex)));
  }

  /**
   * Fetches the comparisons that link one deck, on either side (RA-2703) — the first half of the delete
   * confirm dialog's impact preview.
   *
   * 🔴 **This has to come from the server.** The Decks and Comparisons sections of the modal have
   * independent active category tabs, so the comparison set the client already holds can be a different
   * category from the deck being deleted and miss its comparisons entirely.
   *
   * 🔴 **A page, not the whole set.** `pagesize` is sent explicitly rather than inherited from the endpoint's
   * `@DefaultValue("25")`, so the truncation is a decision here instead of a silent coupling to a backend
   * default. The caller must therefore take its **count** from `totalRecords` and treat `records` as a
   * preview — see {@link SlideDeckDeleteResult} and the dialog's `comparisonTotal`.
   *
   * ⚠️ `slide_deck_id` here is the deck's **row id**, not the producer's SHA-256 business key — the backend
   * filter is typed `Long` for exactly that reason.
   * @param {number} companyId the owning company id
   * @param {number} deckId the slide deck row id
   * @returns {Observable<PaginatedList<SlideDeckComparisonMeta>>}
   */
  public getComparisonsForDeck (companyId: number, deckId: number): Observable<PaginatedList<SlideDeckComparisonMeta>> {
    // Delegates rather than reassembling the query: getComparisons already merges company_id and
    // include_payload=false, and a second copy would miss anything later added to that contract.
    return this.getComparisons(companyId, { slide_deck_id: deckId, pagesize: DECK_IMPACT_PAGE_SIZE });
  }

  /**
   * Fetches the still-generating comparison requests that name one deck, on either side (RA-2703) — the
   * second half of the delete confirm dialog's impact preview, which is what lets it warn that N comparisons
   * currently being generated will be cancelled.
   *
   * Only the count is rendered, so this deliberately asks for a **single** record and reads `totalRecords`.
   * `status=PENDING` comes from {@link getRequests}' own default — a settled request cannot be cancelled by a
   * deletion.
   * @param {number} companyId the owning company id
   * @param {number} deckId the slide deck row id
   * @returns {Observable<PaginatedList<SlideDeckComparisonRequest>>}
   */
  public getPendingComparisonRequestsForDeck (
    companyId: number, deckId: number): Observable<PaginatedList<SlideDeckComparisonRequest>> {
    return this.getRequests(companyId, { slide_deck_id: deckId, pagesize: 1 });
  }

}
